import { useState } from "react";

import { ChevronDown, ChevronUp, ICON_STROKE } from "@/shared/components/icons";
import type { SkillState } from "@/shared/models";
import { ClickableSkillRow } from "@/features/skills/components/SkillRow";
import { barFill, overallLevel } from "@/features/skills/lib/skillStats";

const CATEGORY_ORDER = [
  "Preflop",
  "Flop",
  "Turn",
  "River",
  "Pot types",
  "Exploits & framework",
];

/** Expandable per-category sections, canonical order first. */
export function CategorySections({ skills }: { skills: SkillState[] }) {
  const byCategory = new Map<string, SkillState[]>();
  for (const s of skills) {
    const arr = byCategory.get(s.category) ?? [];
    arr.push(s);
    byCategory.set(s.category, arr);
  }
  const ordered: string[] = [];
  for (const c of CATEGORY_ORDER) if (byCategory.has(c)) ordered.push(c);
  for (const c of byCategory.keys()) if (!ordered.includes(c)) ordered.push(c);

  return (
    <div className="space-y-3">
      {ordered.map((category) => (
        <CategorySection
          key={category}
          category={category}
          skills={byCategory.get(category)!}
        />
      ))}
    </div>
  );
}

function CategorySection({
  category,
  skills,
}: {
  category: string;
  skills: SkillState[];
}) {
  const [expanded, setExpanded] = useState(false);
  const mapped = skills.filter((s) => s.signalCount > 0);
  // Confidence-weighted, same definition as the overall gauge.
  const avg = overallLevel(skills);

  return (
    <section className="rounded-xl border border-border bg-surface overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-4 flex items-center gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-text">{category}</div>
          <div className="h-1.5 bg-border rounded-full overflow-hidden mt-2">
            <div
              className="h-full rounded-full"
              style={{ width: `${Math.max(2, avg)}%`, backgroundColor: barFill(avg, mapped.length === 0) }}
            />
          </div>
        </div>
        <span className="font-mono text-sm tabular-nums w-10 text-right">
          {mapped.length === 0 ? "–" : avg}
        </span>
        {expanded ? (
          <ChevronUp size={16} strokeWidth={ICON_STROKE} className="text-text-muted/70" />
        ) : (
          <ChevronDown size={16} strokeWidth={ICON_STROKE} className="text-text-muted/70" />
        )}
      </button>
      {expanded && (
        <div className="border-t border-border/50 p-3 space-y-1.5">
          {skills.map((s) => (
            <ClickableSkillRow key={s.skillId} skill={s} />
          ))}
        </div>
      )}
    </section>
  );
}
